import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { useAuth0 } from '@auth0/auth0-react';
import { FullscreenLoading } from '@farmshare/ui-components';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { ReactNode, useEffect, useState } from 'react';

import { getApolloClient } from 'lib/graphql/client';
import { Container } from '@mui/material';

interface AuthApolloProviderProps {
  children: ReactNode;
}

export function AuthApolloProvider({ children }: AuthApolloProviderProps) {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    isLoading: isA0Loading,
    logout,
  } = useAuth0();

  const [accessToken, setAccessToken] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [apolloClient, setApolloClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  if (!import.meta.env.PROD) {
    loadDevMessages();
    loadErrorMessages();
  }

  const updateAccessToken = (token: string) => {
    const decodedToken: JwtPayload = jwtDecode(token);
    if (decodedToken?.exp && decodedToken.exp * 1000 < Date.now()) {
      logout();
      return;
    }

    setAccessToken(token);
  };

  useEffect(() => {
    if (isA0Loading) return;

    if (isAuthenticated) {
      getAccessTokenSilently()
        .then(updateAccessToken)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, isA0Loading, isAuthenticated]);

  useEffect(() => {
    if (loading) return; // Skip if still loading access token

    let isMounted = true;

    const initializeApolloClient = async () => {
      try {
        const client = await getApolloClient(accessToken || '');
        if (isMounted) {
          setApolloClient(client);
        }
      } catch (_error) {
        // Failed to initialize Apollo client
        if (isMounted) {
          setApolloClient(null);
        }
      }
    };

    initializeApolloClient();

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false;
    };
  }, [accessToken, loading]);

  // If we're still loading the access token, Auth0 state, or Apollo client, show a loading indicator
  if (loading || !apolloClient) {
    return (
      <Container>
        <FullscreenLoading />
      </Container>
    );
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
